import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { capturePostHogEvent } from "@/lib/posthog";
import logoUrl from "@assets/edufocal-logo-min-50292a59_1758905778671.png";

type Step = "identifier" | "code";

export default function ParentLogin() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [step, setStep] = useState<Step>("identifier");
  const [identifier, setIdentifier] = useState("");
  const [channel, setChannel] = useState<"phone" | "email" | null>(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiRequest("GET", "/api/parent/me")
      .then(() => setLocation("/parent"))
      .catch(() => {});
  }, [setLocation]);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || identifier.trim().length < 3) return;
    setSubmitting(true);
    try {
      const res = await apiRequest("POST", "/api/parent/auth/request", { identifier: identifier.trim() });
      const data = await res.json();
      setChannel(data.channel);
      setStep("code");
      capturePostHogEvent("parent_auth_code_requested", { channel: data.channel });
      toast({ title: "Code sent", description: data.message });
    } catch (err: any) {
      toast({ title: "Could not send code", description: err?.message ?? "Try again", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || code.length !== 6) return;
    setSubmitting(true);
    try {
      await apiRequest("POST", "/api/parent/auth/verify", { identifier: identifier.trim(), code });
      capturePostHogEvent("parent_auth_verified", { channel });
      setLocation("/parent");
    } catch (err: any) {
      toast({ title: "Verification failed", description: err?.message ?? "Invalid code", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const channelLabel = channel === "phone" ? "WhatsApp" : "email";

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <img src={logoUrl} alt="EduFocal Academy" className="h-10 w-auto" />
          </div>
          {step === "identifier" ? (
            <>
              <CardTitle className="text-center text-2xl">Parent Dashboard</CardTitle>
              <CardDescription className="text-center">
                Enter the phone number or email on your child's account. We will text a 6-digit code via WhatsApp, or email it if you used an email.
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-center text-2xl">Enter your code</CardTitle>
              <CardDescription className="text-center">
                We sent a 6-digit code to your {channelLabel}. It expires in 15 minutes.
              </CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent>
          {step === "identifier" ? (
            <form onSubmit={handleRequest} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifier">Phone or email</Label>
                <Input
                  id="identifier"
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="e.g. 876 555 1234 or you@example.com"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  className="h-12"
                  data-testid="parent-login-identifier"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-12">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  "Send code"
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Verification code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  className="h-12 text-center tracking-[0.5em] text-lg"
                  data-testid="parent-login-code"
                  autoFocus
                />
              </div>
              <Button type="submit" disabled={submitting || code.length !== 6} className="w-full h-12">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying…
                  </>
                ) : (
                  "Verify"
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setStep("identifier");
                  setCode("");
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Use a different phone or email
              </Button>
            </form>
          )}

          <div className="pt-4 mt-4 border-t text-center text-sm text-muted-foreground">
            <a href="/" className="inline-flex items-center gap-1 hover:text-foreground">
              <ArrowLeft className="h-3 w-3" />
              Back to EduFocal
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
